import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Compressor from "compressorjs";

import { i18n } from "../../translate/i18n";

import api, { getMediaUrl } from "../../services/api";
import toastError from "../../errors/toastError";
import { Stack } from "@mui/material";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  btnWrapper: {
    position: "relative"
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));


const FlowBuilderAddImgModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  const [activeModal, setActiveModal] = useState(false);

  const [loading, setLoading] = useState(false);

  const [preview, setPreview] = useState();

  const [oldImage, setOldImage] = useState();

  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilder.image.addTitle") || "Adicionar imagem ao fluxo",
    btn: i18n.t("common.add") || "Adicionar"
  });

  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [antiBan, setAntiBan] = useState(false);
  const [medias, setMedias] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: i18n.t("flowBuilder.image.editTitle") || "Editar imagem",
        btn: i18n.t("common.save")
      });
      setOldImage(data.data.url);
      setName(data.data.name || "");
      setCaption(data.data.caption || "");
      setAntiBan(!!data.data.antiBan);
      // [ANTIGRAVITY] Robust companyId detection
      const companyId = user?.companyId || localStorage.getItem("companyId") || 1;
      setPreview(getMediaUrl(data.data.url, companyId));
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: i18n.t("flowBuilder.image.addTitle") || "Adicionar imagem ao fluxo",
        btn: i18n.t("common.add") || "Adicionar"
      });
      setName("");
      setCaption("");
      setAntiBan(false);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async () => {
    if (open === "edit" && medias.length === 0) {
      handleClose();
      onUpdate({
        ...data,
        data: {
          ...data.data,
          name: name,
          caption: caption,
          antiBan: antiBan,
          url: data.data.url ? data.data.url.split(/[/\\]/).pop() : ""
        }
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("fromMe", true);
    formData.append("typeArch", "flow");

    const processMedia = async (media) => {
      if (!media) return;
      if (media.type.split("/")[0] === "image") {
        return new Promise((resolve, reject) => {
          new Compressor(media, {
            quality: 0.7,
            success(compressedResult) {
              // [ANTIGRAVITY] FIX: Force original filename, otherwise it sends as "blob"
              formData.append("medias", compressedResult, media.name);
              formData.append("body", media.name);
              resolve();
            },
            error(err) {
              console.log(err.message);
              // Fallback: try to upload original on compression error
              formData.append("medias", media);
              formData.append("body", media.name);
              resolve();
            }
          });
        });
      } else {
        formData.append("medias", media);
        formData.append("body", media.name);
        return Promise.resolve();
      }
    };

    try {
      await Promise.all(medias.map(media => processMedia(media)));
      const { data: resData } = await api.post("/flowbuilder/img", formData);

      handleClose();

      if (open === "edit") {
        onUpdate({
          ...data,
          data: {
            ...data.data,
            name: name,
            caption: caption,
            antiBan: antiBan,
            url: resData.name ? resData.name.split(/[/\\]/).pop() : ""
          }
        });
      } else {
        onSave({
          url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
          name: name,
          caption: caption,
          antiBan: antiBan
        });
      }

      toast.success(i18n.t("flowBuilder.image.success") || "Imagem adicionada com sucesso!");
      setMedias([]);
      setPreview();
      setName("");
      setCaption("");
      setAntiBan(false);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    if (e.target.files[0].size > 20000000) { // Keep 20MB limit consistent with video or change to 2MB as per text? Original code had 20000000 but text said 2MB. Sticking to 20MB logic for consistency but text says 2MB.
      // Actually original code in image modal had 2000000 check which is 2MB. Wait line 203 shows 2000000.
      // Let's stick to the code I read in view_file: line 203 says `if (e.target.files[0].size > 2000000)`.
    }

    if (e.target.files[0].size > 2000000) {
      toast.error(i18n.t("flowBuilder.image.tooLarge") || "Arquivo é muito grande! 2MB máximo")
      return
    }

    const selectedMedias = Array.from(e.target.files);
    setPreview(URL.createObjectURL(e.target.files[0]));
    setMedias(selectedMedias);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack gap={"16px"}>
              <TextField
                label={i18n.t("flowBuilder.form.name") || "Nome"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
              />
              {preview && (
                <Stack direction={'row'} justifyContent={'center'} sx={{ bgcolor: '#f0f0f0', borderRadius: '8px', overflow: 'hidden', padding: '10px' }}>
                  <img src={preview} style={{ maxWidth: "100%", maxHeight: "350px", objectFit: "contain" }} alt="Preview" />
                </Stack>
              )}
              <TextField
                label="Texto/Caption (opcional)"
                placeholder="Agrega un texto que se enviará junto con la imagen"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
                helperText="El texto se enviará como caption de la imagen (como en WhatsApp)"
              />
              <div style={{
                marginTop: "4px",
                marginBottom: "4px",
                background: antiBan ? "rgba(74,222,128,0.07)" : "rgba(128,128,128,0.1)",
                border: `1px solid ${antiBan ? "#4ade80" : "rgba(128,128,128,0.2)"}`,
                borderRadius: "8px",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "all 0.3s ease",
                width: "100%"
              }}>
                <div>
                  <Typography variant="subtitle2" style={{ fontWeight: 700, color: antiBan ? "#4ade80" : "inherit" }}>
                    🛡️ AntiBan IA {antiBan ? "[Activado]" : "[Desactivado]"}
                  </Typography>
                  <Typography variant="caption" style={{ color: "gray", display: "block" }}>
                    {antiBan
                      ? "Re-escribe el texto usando IA para variar los envíos y evitar bloqueos. (Consume saldo IA)"
                      : "Envía el texto exactamente como se configuró sin usar saldo de IA."}
                  </Typography>
                </div>
                <Switch
                  checked={antiBan}
                  onChange={(e) => setAntiBan(e.target.checked)}
                  color="primary"
                />
              </div>
              {!loading && (
                <Button variant="contained" component="label">
                  {i18n.t("flowBuilder.image.upload") || "Enviar imagem"}
                  <input
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    disabled={loading}
                    hidden
                    onChange={handleChangeMedias}
                  />
                </Button>
              )}
              {loading && (
                <>
                  <Stack justifyContent={"center"} alignSelf={"center"}>
                    <CircularProgress />
                  </Stack>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            {!loading && (
              <>
                <Button onClick={() => {
                    handleClose();
                    setMedias([]);
                    setPreview();
                    setCaption("");
                  }} 
                  variant="outlined" 
                  style={{ borderColor: "#ff4d4f", color: "#ff4d4f", textTransform: "none", fontWeight: "bold", borderRadius: "8px" }}
                >
                  {i18n.t("common.cancel") || "Cancelar"}
                </Button>
                <Button 
                  type="submit" 
                  onClick={() => handleSaveContact()} 
                  disabled={loading} 
                  variant="contained" 
                  style={{ backgroundColor: "#7367F0", color: "#fff", textTransform: "none", fontWeight: "bold", borderRadius: "8px", boxShadow: "0 4px 14px 0 rgba(115, 103, 240, 0.39)" }}
                >
                  {`${labels.btn}`}
                </Button>
              </>
            )}
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddImgModal;
